import React, { useState } from 'react';
import { 
  Box,
  Container, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  CircularProgress,
  Alert,
  Link,
  Grid,
  InputAdornment,
  IconButton,
  Fade,
  useTheme,
  useMediaQuery,
  GlobalStyles
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate, Navigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useUser } from '../context/UserContext';
import { motion } from 'framer-motion';

const Login = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  
  const { login, currentUser, error } = useAuth();
  const { setUser } = useUser();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // If user is already logged in, redirect to home
  if (currentUser) {
    return <Navigate to="/" />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setLoading(true);
    console.log('Login form submitted');
    
    try {
      console.log('Attempting login with', formData.username);
      await login(formData);
      console.log('Login successful');
      // Fetch user info after successful login
      const { getUserInfo } = await import('../services/userService');
      const userInfo = await getUserInfo();
      setUser(userInfo);
      navigate('/');
    } catch (err) {
      console.error('Login failed:', err);
      setErrorMessage(
        err.response?.data?.detail || 
        err.response?.data?.non_field_errors?.[0] || 
        'Login failed. Please check your credentials or network connection.'
      );
      if (err.response) {
        console.error('Error response data:', err.response.data);
        console.error('Error response status:', err.response.status);
      } else if (err.request) {
        console.error('Error request:', err.request);
      } else {
        console.error('Error message:', err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const formVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        delay: 0.3,
        duration: 0.5
      }
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          '*': {
            boxSizing: 'border-box',
          },
          html: {
            fontSize: '16px',
            '@media (max-width: 600px)': {
              fontSize: '14px',
            },
            '@media (max-width: 400px)': {
              fontSize: '13px',
            }
          },
          body: {
            margin: 0,
            padding: 0,
            background: '#ffffff',
            minHeight: '100vh',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            overflowX: 'hidden',
            width: '100%',
            maxWidth: '100vw'
          },
          // Override global styles specifically for login page
          '.login-container.MuiContainer-root': {
            maxWidth: '392px !important',
            width: 'auto !important',
            '@media (max-width: 600px)': {
              maxWidth: '336px !important',
            },
            '@media (min-width: 600px)': {
              maxWidth: '364px !important',
            },
            '@media (min-width: 900px)': {
              maxWidth: '392px !important',
            }
          },
          '.login-paper.MuiPaper-root': {
            maxWidth: '364px !important',
            width: '100% !important',
            '@media (max-width: 600px)': {
              maxWidth: '308px !important',
            },
            '@media (min-width: 600px)': {
              maxWidth: '336px !important',
            },
            '@media (min-width: 900px)': {
              maxWidth: '364px !important',
            }
          }
        }}
      />
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          maxWidth: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#ffffff',
          position: 'relative',
          overflow: 'hidden',
          px: { xs: 0.5, sm: 1 },
          py: { xs: 0.5, sm: 1 },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.08"%3E%3Ccircle cx="30" cy="30" r="2"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            animation: 'float 20s ease-in-out infinite',
          },
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px)' },
            '50%': { transform: 'translateY(-20px)' }
          }
        }}
      >
        <Container 
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          maxWidth={false}
          className="login-container"
          sx={{
            px: { xs: 0.5, sm: 1 },
            py: { xs: 0.5, sm: 1 },
            width: 'auto',
            maxWidth: { xs: '336px', sm: '364px', md: '392px' }
          }}
        >
          <Paper 
            elevation={8}
            className="login-paper"
            sx={{ 
              p: { xs: 1.5, sm: 2 },
              borderRadius: { xs: 2, sm: 2 },
              background: '#ffffff',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
              position: 'relative',
              overflow: 'hidden',
              width: '100%',
              maxWidth: { xs: '308px', sm: '336px', md: '364px' },
              mx: 'auto',
              minHeight: { xs: 'auto', sm: '490px' },
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              border: '1px solid #e0e0e0',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '3px',
                background: 'linear-gradient(90deg, #00843D 0%, #00A550 50%, #FF0000 100%)',
              }
            }}
          >
            {/* Logo and Title */}
            <Box 
              sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center', 
                mb: { xs: 1.5, sm: 2 }
              }}
            >
              <Box 
                component={motion.div}
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1.5
                }}
              >
                <Box 
                  component="span" 
                  sx={{ 
                    color: '#FF0000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1,
                    filter: 'drop-shadow(0 0 8px rgba(255,0,0,0.3))',
                    animation: 'pulse 2s infinite',
                    '@keyframes pulse': {
                      '0%': { opacity: 0.8, transform: 'scale(1)' },
                      '50%': { opacity: 1, transform: 'scale(1.1)' },
                      '100%': { opacity: 0.8, transform: 'scale(1)' }
                    }
                  }}
                >
                  <StarIcon sx={{ fontSize: { xs: '1.8rem', sm: '2rem' }, color: '#FF0000' }} />
                </Box>
                <Typography 
                  component="h1" 
                  variant={isMobile ? "h5" : "h4"}
                  sx={{ 
                    fontWeight: 800, 
                    background: 'linear-gradient(45deg, #00843D 30%, #00A550 90%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '0.05em',
                    textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  AMS
                </Typography>
              </Box>
              
              <Typography 
                component="h2" 
                variant={isMobile ? "subtitle1" : "h6"}
                align="center" 
                sx={{
                  fontWeight: 600,
                  color: '#333333',
                  letterSpacing: '0.02em',
                  mb: 0.5
                }}
              >
                Welcome Back
              </Typography>
              
              <Typography 
                variant="body2"
                align="center" 
                sx={{
                  color: '#666666',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}
              >
                Accrual Management System
              </Typography>
            </Box>
            
            {/* Error Alert */}
            <Fade in={!!(errorMessage || error)}>
              <Box sx={{ mb: 2 }}>
                {(errorMessage || error) && (
                  <Alert 
                    severity="error" 
                    sx={{ 
                      borderRadius: 2,
                      '& .MuiAlert-message': {
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }
                    }}
                  >
                    {errorMessage || error}
                  </Alert>
                )}
              </Box>
            </Fade>
            
            {/* Login Form */}
            <Box 
              component={motion.form}
              variants={formVariants}
              initial="hidden"
              animate="visible"
              onSubmit={handleSubmit} 
              noValidate 
              sx={{ mt: 1 }}
            >
              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                value={formData.username}
                onChange={handleChange}
                disabled={loading}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: '#00843D', fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 2, 
                    py: { xs: 0.25, sm: 0.5 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }
                }}
                sx={{
                  mb: { xs: 1.5, sm: 2 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover fieldset': {
                      borderColor: '#00A550',
                      borderWidth: '2px'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00843D',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 3px rgba(0, 132, 61, 0.1)'
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&.Mui-focused': {
                      color: '#00843D',
                    }
                  }
                }}
              />
              
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formData.password}
                onChange={handleChange}
                disabled={loading}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: '#00843D', fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={togglePasswordVisibility}
                        edge="end"
                        size="small"
                        sx={{ color: '#00843D' }}
                      >
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 2, 
                    py: { xs: 0.25, sm: 0.5 },
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }
                }}
                sx={{
                  mb: { xs: 2, sm: 2.5 },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover fieldset': {
                      borderColor: '#00A550',
                      borderWidth: '2px'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00843D',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 3px rgba(0, 132, 61, 0.1)'
                    },
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    '&.Mui-focused': {
                      color: '#00843D',
                    }
                  }
                }}
              />
              
              <Button
                component={motion.button}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                fullWidth
                variant="contained"
                size="medium"
                sx={{ 
                  mt: { xs: 0.5, sm: 1 }, 
                  mb: { xs: 2, sm: 2.5 }, 
                  py: { xs: 1, sm: 1.25 },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: { xs: '0.875rem', sm: '1rem' },
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #00843D 30%, #00A550 90%)',
                  boxShadow: '0 6px 20px rgba(0, 132, 61, 0.3)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #007035 30%, #008F45 90%)',
                    boxShadow: '0 8px 25px rgba(0, 132, 61, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  '&:disabled': {
                    background: 'linear-gradient(45deg, #cccccc 30%, #dddddd 90%)',
                    boxShadow: 'none'
                  }
                }}
                disabled={loading || !formData.username || !formData.password}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: '#ffffff' }} />
                    <span style={{ color: '#ffffff' }}>Signing In...</span>
                  </Box>
                ) : (
                  <span style={{ color: '#ffffff' }}>Sign In</span>
                )}
              </Button>
              
              <Grid container justifyContent="center">
                <Grid item>
                  <Link 
                    component={RouterLink} 
                    to="/forgot-password" 
                    variant="body2"
                    underline="hover"
                    sx={{
                      color: '#00843D',
                      fontWeight: 500,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: '#00A550',
                        textDecoration: 'none',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    Forgot your password?
                  </Link>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: { xs: 2, sm: 2.5 }, textAlign: 'center' }}>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    display: 'block', 
                    mb: 0.5,
                    fontSize: { xs: '0.65rem', sm: '0.75rem' }
                  }}
                >
                  © {new Date().getFullYear()} AMS - Accrual Management System
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                    opacity: 0.7
                  }}
                >
                  Powered by Heineken Vietnam
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Login;
